import { httpClient } from '../../../shared/api/httpClient';
import { Ticket, Message, TicketCategory, AuditEvent } from '../model/types';

const categoriesCache = new Map<string, { data: TicketCategory[]; expiresAt: number }>();
const CATEGORIES_CACHE_TTL_MS = 60_000;

export async function getTickets(filters?: {
  status?: string[];
  search?: string;
  site?: string;
  priority?: string;
  clientId?: string;
  limit?: number;
  offset?: number;
  updatedAfter?: string;
  orderBy?: 'created_asc' | 'created_desc' | 'updated_asc' | 'updated_desc';
}): Promise<Ticket[]> {
  const params = new URLSearchParams();
  if (filters?.status) {
    filters.status.forEach(s => params.append('status', s));
  }
  if (filters?.search) {
    params.append('search', filters.search);
  }
  if (filters?.site) {
    params.append('site', filters.site);
  }
  if (filters?.priority) {
    params.append('priority', filters.priority);
  }
  if (filters?.clientId) {
    params.append('clientId', filters.clientId);
  }
  if (typeof filters?.limit === 'number') {
    params.append('limit', String(filters.limit));
  }
  if (typeof filters?.offset === 'number') {
    params.append('offset', String(filters.offset));
  }
  if (filters?.updatedAfter) {
    params.append('updatedAfter', filters.updatedAfter);
  }
  if (filters?.orderBy) {
    params.append('orderBy', filters.orderBy);
  }
  
  const queryString = params.toString();
  return httpClient.get<Ticket[]>(`/tickets/${queryString ? `?${queryString}` : ''}`);
}

export async function getTicketById(id: string): Promise<Ticket | null> {
  try {
    return await httpClient.get<Ticket>(`/tickets/${id}/`);
  } catch (error) {
    return null;
  }
}

export async function createTicket(data: {
  title: string;
  site: string;
  clientId?: string;
  clientName?: string;
  clientEmail: string;
  priority: string;
  category?: string;
  initialMessage?: string;
}): Promise<Ticket> {
  return httpClient.post<Ticket>('/tickets/', {
    title: data.title,
    site: data.site,
    clientId: data.clientId,
    clientName: data.clientName,
    clientEmail: data.clientEmail,
    priority: data.priority,
    category: data.category,
    initialMessage: data.initialMessage,
  });
}

export async function updateTicket(id: string, data: Partial<Ticket>): Promise<Ticket> {
  return httpClient.put<Ticket>(`/tickets/${id}/`, {
    status: data.status,
    site: data.site,
    priority: data.priority,
    category: data.category,
    assignedTo: data.assignedTo,
  });
}

export async function getTicketMessages(ticketId: string): Promise<Message[]> {
  return httpClient.get<Message[]>(`/tickets/${ticketId}/messages/`);
}

export async function sendMessage(
  ticketId: string, 
  content: string, 
  attachments?: File[],
  isInternal?: boolean
): Promise<Message> {
  const formData = new FormData();
  formData.append('content', content);
  if (isInternal !== undefined) {
    formData.append('is_internal', String(isInternal));
  }
  if (attachments) {
    attachments.forEach(file => formData.append('attachments', file));
  }
  
  const timeout = attachments && attachments.length > 0 ? 60000 : 30000;
  
  return httpClient.post<Message>(`/tickets/${ticketId}/send_message/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout,
  });
}

export async function getTicketCategories(site?: string, all: boolean = true): Promise<TicketCategory[]> {
  const cacheKey = `${site || 'all'}:${all ? 'all' : 'restricted'}`;
  const cached = categoriesCache.get(cacheKey);
  const now = Date.now();
  if (cached && cached.expiresAt > now) {
    return cached.data;
  }

  const params = new URLSearchParams();
  if (site) {
    params.append('site', site);
  }
  if (all) {
    params.append('all', 'true');
  }

  const data = await httpClient.get<TicketCategory[]>(`/tickets/categories/?${params.toString()}`);
  categoriesCache.set(cacheKey, {
    data,
    expiresAt: now + CATEGORIES_CACHE_TTL_MS,
  });
  return data;
}

export async function getTicketAuditHistory(ticketId: string): Promise<AuditEvent[]> {
  return httpClient.get<AuditEvent[]>(`/tickets/${ticketId}/audit/`);
}

export async function createYouTrackIssue(ticketId: string): Promise<{ success: boolean; issueId: string; issueUrl: string }> {
  return httpClient.post<{ success: boolean; issueId: string; issueUrl: string }>(
    `/tickets/${ticketId}/create_youtrack_issue/`,
    undefined,
    { timeout: 30000 },
  );
}
